import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  calculateAdminAnalyticsSummary,
  type AdminDailyAnalyticsData,
} from '../domain/adminAnalytics';

interface AdminAnalyticsDashboardProps {
  data: AdminDailyAnalyticsData;
}

const formatMetric = (value: number): string =>
  new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(value);

const AdminAnalyticsDashboard: React.FC<AdminAnalyticsDashboardProps> = ({
  data,
}) => {
  const summary = useMemo(
    () => calculateAdminAnalyticsSummary(data.daily),
    [data.daily],
  );
  const maxUniqueVisitors = Math.max(
    1,
    ...data.daily.map((day) => day.uniqueVisitors),
  );

  return (
    <Dashboard>
      <Cards>
        <MetricCard>
          <MetricLabel>외부 기간 총 방문</MetricLabel>
          <MetricValue>{formatMetric(summary.totalVisits)}</MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>외부 일평균 순방문자</MetricLabel>
          <MetricValue>
            {formatMetric(summary.averageDailyUniqueVisitors)}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>외부 최고 일 순방문자</MetricLabel>
          <MetricValue>
            {formatMetric(summary.peakDailyUniqueVisitors)}
          </MetricValue>
        </MetricCard>
        <MetricCard>
          <MetricLabel>외부 일평균 방문 아카이브</MetricLabel>
          <MetricValue>
            {formatMetric(summary.averageDailyVisitedArchives)}
          </MetricValue>
        </MetricCard>
      </Cards>

      <ChartSection>
        <SectionTitle>일별 순방문자</SectionTitle>
        <Chart aria-label="일별 순방문자 막대 그래프">
          {data.daily.map((day) => (
            <BarColumn
              key={day.date}
              title={`${day.date}: ${day.uniqueVisitors}명`}
            >
              <BarValue>{day.uniqueVisitors}</BarValue>
              <BarTrack>
                <Bar
                  $height={(day.uniqueVisitors / maxUniqueVisitors) * 100}
                />
              </BarTrack>
              <BarDate>{day.date.slice(5)}</BarDate>
            </BarColumn>
          ))}
        </Chart>
      </ChartSection>

      <Note>
        소유자 방문은 포함하지 않습니다. 순방문자는 날짜별로 중복
        제거되며, 일별 순방문자를 더한 값을 기간 순방문자로 표시하지
        않습니다.
      </Note>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>날짜</th>
              <th>방문</th>
              <th>순방문자</th>
              <th>탭 세션</th>
              <th>방문 아카이브</th>
            </tr>
          </thead>
          <tbody>
            {[...data.daily].reverse().map((day) => (
              <tr key={day.date}>
                <td>{day.date}</td>
                <td>{formatMetric(day.visits)}</td>
                <td>{formatMetric(day.uniqueVisitors)}</td>
                <td>{formatMetric(day.sessions)}</td>
                <td>{formatMetric(day.visitedArchives)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </Dashboard>
  );
};

export default AdminAnalyticsDashboard;

const Dashboard = styled.section`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 20px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
`;

const MetricCard = styled.div`
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: ${({ theme }) => theme.colors.color_Alpha_031};
`;

const MetricLabel = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const MetricValue = styled.div`
  ${({ theme }) => theme.typography.Body_01_1};
`;

const ChartSection = styled.section`
  min-height: 230px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Chart = styled.div`
  height: 190px;
  display: flex;
  align-items: stretch;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const BarColumn = styled.div`
  width: 28px;
  min-width: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BarValue = styled.span`
  height: 18px;
  ${({ theme }) => theme.typography.Footer_02};
`;

const BarTrack = styled.div`
  width: 100%;
  min-height: 140px;
  display: flex;
  align-items: flex-end;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Bar = styled.div<{ $height: number }>`
  width: 100%;
  min-height: ${({ $height }) => ($height > 0 ? '2px' : '0')};
  height: ${({ $height }) => `${$height}%`};
  background: ${({ theme }) => theme.colors.color_Gray_02};
`;

const BarDate = styled.span`
  margin-top: 4px;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Footer_02};
`;

const Note = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const TableWrapper = styled.div`
  min-height: 200px;
  overflow: auto;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${({ theme }) => theme.typography.Body_04};

  th,
  td {
    padding: 8px;
    text-align: right;
    border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }
`;
